import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import ProfileAvatar from "@/components/ProfileAvatar";
import { Loader2, X } from "lucide-react";

/**
 * Phase 9 — 4-digit PIN entry. Verifies via POST /api/me/profiles/{id}/verify-pin
 * before unlocking. On success calls onSuccess(profile). Cancel calls onCancel.
 *
 * Renders four <input maxLength=1> tiles with auto-advance focus. Numeric only.
 */
export default function PinModal({ profile, onSuccess, onCancel }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  if (!profile) return null;

  const setDigit = (i, v) => {
    const cleaned = (v || "").replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = cleaned;
    setDigits(next);
    if (cleaned && i < 3) inputs.current[i + 1]?.focus();
    if (!cleaned && i > 0) {
      // when a tile is wiped via backspace handler, do nothing extra
    }
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
    if (e.key === "Enter") submit();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (!text) return;
    e.preventDefault();
    const next = ["", "", "", ""];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    inputs.current[Math.min(text.length, 3)]?.focus();
  };

  const submit = async () => {
    const pin = digits.join("");
    if (pin.length !== 4) {
      setError("Enter all 4 digits");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await api.post(`/me/profiles/${profile.id}/verify-pin`, { pin });
      onSuccess?.(profile);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Incorrect PIN";
      setError(msg);
      setDigits(["", "", "", ""]);
      inputs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/75 backdrop-blur-md p-4"
      onClick={() => onCancel?.()}
      data-testid="pin-modal"
    >
      <div
        className="relative w-full max-w-sm bg-[#0a0a0a] border border-white/10 p-7 md:p-9"
        onClick={(e) => e.stopPropagation()}
        data-testid="pin-modal-card"
      >
        <button
          type="button"
          onClick={() => onCancel?.()}
          className="absolute top-3 right-3 text-white/55 hover:text-white grid place-items-center w-8 h-8"
          data-testid="pin-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-col items-center gap-3">
          <ProfileAvatar profile={profile} size="lg" />
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/55 mt-2">Enter PIN</p>
          <h3 className="font-display font-bold text-2xl tracking-tight" data-testid="pin-modal-name">{profile.name}</h3>
          <p className="text-white/55 text-sm font-light">This profile is locked. Enter the 4-digit PIN.</p>
        </div>

        <div className="mt-7 grid grid-cols-4 gap-3" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              className="h-14 text-center text-2xl bg-black/40 border border-white/20 focus:outline-none focus:border-white text-white tracking-tighter"
              data-testid={`pin-digit-${i}`}
            />
          ))}
        </div>
        {error && (
          <div className="mt-4 text-[11px] uppercase tracking-[0.25em] text-rose-300 text-center" data-testid="pin-modal-error">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={loading}
          className="mt-6 w-full h-12 bg-white text-black font-bold uppercase tracking-[0.25em] text-xs hover:bg-amber-300 transition-colors disabled:opacity-50"
          data-testid="pin-modal-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Unlock"}
        </button>
        <button
          type="button"
          onClick={() => onCancel?.()}
          className="mt-3 w-full text-[11px] uppercase tracking-[0.3em] text-white/55 hover:text-white"
          data-testid="pin-modal-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
